import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AdminUser } from '@/types/auth'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'

interface AuthState {
    user: AdminUser | null
    token: string | null
    refreshToken: string | null
    isAuthenticated: boolean
    isLoading: boolean
    setUser: (user: AdminUser | null) => void
    setToken: (token: string | null) => void
    setRefreshToken: (refreshToken: string | null) => void
    login: (user: AdminUser) => void
    logout: () => void
    setLoading: (isLoading: boolean) => void
}

export const useAuthStore = create<AuthState>()(
    persist(
        set => ({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,

            setUser: user => {
                if (user) {
                    setSentryUser(user)
                } else {
                    clearSentryUser()
                }
                set({
                    user,
                    isAuthenticated: !!user,
                })
            },

            setToken: token => set({ token }),

            setRefreshToken: refreshToken => set({ refreshToken }),

            login: user => {
                setSentryUser(user)
                set({
                    user,
                    token: user.accessToken,
                    refreshToken: user.refreshToken,
                    isAuthenticated: true,
                    isLoading: false,
                })
            },

            logout: () => {
                clearSentryUser()
                set({
                    user: null,
                    token: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    isLoading: false,
                })
            },

            setLoading: isLoading => set({ isLoading }),
        }),
        {
            name: 'auth-store',
        }
    )
)
